import { getDataMode } from '@/config/data-mode';
import {
  ApiClient,
  ApiError,
  type ConsentType,
  type ConsentSignature,
  type ConsentDecisionMap,
  type PreguntaAsistenteInput,
  type RespuestaAsistente,
} from '@/services/api-client';
import { respuestaAsistenteMock } from '@/lib/asistente';
import { DataService as MockDataService, type UserGoal } from '@/services/DataService';
import { buildMockResult } from '@/services/nutrition-analysis';
import { analysisErrorMessage } from '@/services/analysis-errors';
// Solo el CONTRATO en estático: son tipos y cuatro funciones de mapeo sin
// dependencias. El análisis de píxeles (detección de plato, portero, recorte)
// se carga BAJO DEMANDA más abajo — mismo patrón que `photo-storage.ts` con el
// SDK de Storage, y por el mismo motivo: `data-adapter` es núcleo y lo alcanza
// casi toda la app, así que quien solo consulta su diario no debe pagar la
// descarga del código que analiza fotos. Se descubrió midiendo: importarlo en
// estático encarecía el arranque de módulos lo bastante como para hacer caer
// por tiempo a una prueba que no tiene nada que ver con el análisis.
import {
  aEntradaTransmitida,
  aTransmitida,
  avisosDeCalidad,
  mensajeDeRechazo,
} from '@/lib/entrada-modelo/contrato';
import { searchFoodsExternal } from '@/services/food-search';
import { deleteMealPhoto } from '@/services/photo-storage';
import { AuthService } from '@/services/AuthService';
import type { AnalysisStatus, NutritionAnalysisResult } from '@/models/analysis';

type MockService = typeof MockDataService;

/** Error de conectividad con el backend en modo `api` (no hay datos que mostrar). */
export class DataUnavailableError extends Error {
  constructor(cause?: unknown) {
    super('No se pudo conectar con el servidor');
    this.name = 'DataUnavailableError';
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

/**
 * En modo demo → devuelve datos mock (legítimo, no hay sesión real).
 * En modo `api` → NUNCA cae a mock: propaga un DataUnavailableError para que la
 * UI muestre un estado de error + "reintentar". En una app clínica, mostrar
 * datos de ejemplo como si fueran del paciente es peor que fallar visiblemente.
 */
async function withApiFallback<T>(
  apiFn: () => Promise<T>,
  mockFn: () => Promise<T>,
): Promise<T> {
  if (getDataMode() !== 'api') {
    return mockFn();
  }
  if (!ApiClient.isConfigured()) {
    throw new DataUnavailableError('VITE_API_BASE_URL no configurada');
  }
  try {
    return await apiFn();
  } catch (err) {
    console.warn('[DataAdapter] fallo de API (modo api, sin fallback a mock):', err);
    throw err instanceof DataUnavailableError ? err : new DataUnavailableError(err);
  }
}

/**
 * Perfil del usuario respetando el modo de datos.
 *
 * Existe porque `getResumenNutricionHoy` y `getDashboardResumen` llamaban
 * directamente a `MockDataService.getUsuario()`, saltándose el `getUsuario` de
 * ESTE módulo (más abajo), que sí pregunta a la API. En modo `api` eso metía
 * el usuario del JSON de demostración en la pantalla del paciente: el saludo
 * decía «Paciente», el objetivo salía como «Pérdida de peso saludable» para
 * todo el mundo, y —más grave— los objetivos nutricionales de la demo
 * (1850 kcal) se usaban como respaldo silencioso cuando el backend no envía
 * `goals`, presentados como si fueran los suyos.
 *
 * Es el mismo defecto que ya se corrigió en `getPesoProgreso` (ver nota ahí) y
 * la misma regla que declara `getEmocionHoyWithApi`: en modo `api` NUNCA se
 * cae al mock, se falla visiblemente.
 */
async function getUsuarioWithApi(): Promise<Awaited<ReturnType<MockService['getUsuario']>>> {
  return withApiFallback(() => ApiClient.getMe(), () => MockDataService.getUsuario());
}

async function getResumenNutricionHoyWithApi(): Promise<
  Awaited<ReturnType<MockService['getResumenNutricionHoy']>>
> {
  return withApiFallback(async () => {
    const usuario = await getUsuarioWithApi();
    const hoy = new Date().toISOString().split('T')[0];
    return ApiClient.getDailySummary(hoy, usuario.nutricion);
  }, () => MockDataService.getResumenNutricionHoy());
}

async function getDashboardResumenWithApi(): Promise<
  Awaited<ReturnType<MockService['getDashboardResumen']>>
> {
  const [usuario, nutricion, actividad, emocion, hidratacion] = await Promise.all([
    getUsuarioWithApi(),
    getResumenNutricionHoyWithApi(),
    getActividadHoyWithApi(),
    getEmocionHoyWithApi(),
    getHidratacionHoyWithApi(),
  ]);
  const hoy = new Date().toISOString().split('T')[0];
  return { usuario, nutricion, actividad, emocion, hidratacion, fecha: hoy };
}

async function getComidasHoyWithApi(): Promise<Awaited<ReturnType<MockService['getComidasHoy']>>> {
  return withApiFallback(
    async () => {
      const [meals, lsMeals] = await Promise.all([
        ApiClient.getMealsToday(),
        MockDataService.getComidasHoy(),
      ]);
      // Array vacío es una respuesta válida (usuario sin comidas hoy).
      // Solo caemos a localStorage cuando la API lanza (lo gestiona withApiFallback).
      if (meals.length === 0) return meals;
      const fotosMap = new Map(lsMeals.map(m => [m.id, m.foto]));
      const merged = meals.map(m => ({ ...m, foto: m.foto ?? fotosMap.get(m.id) }));
      // Persistir URLs de foto de la API en localStorage para acceso offline.
      for (const m of merged) {
        if (m.foto && !fotosMap.get(m.id)) {
          void MockDataService.actualizarFotoComida(m.id, m.foto);
        }
      }
      return merged;
    },
    () => MockDataService.getComidasHoy(),
  );
}

async function getHistorialEmocionalWithApi(
  n: number,
): Promise<Awaited<ReturnType<MockService['getHistorialEmocional']>>> {
  return withApiFallback(
    () => ApiClient.getMoodHistory(n),
    () => MockDataService.getHistorialEmocional(n),
  );
}

async function getEmocionHoyWithApi(): Promise<Awaited<ReturnType<MockService['getEmocionHoy']>>> {
  // Usa withApiFallback como el resto del módulo: en modo `api` NUNCA cae a mock
  // (mostrar el estado emocional de ejemplo como si fuera del paciente es peor que
  // fallar visiblemente). Antes tenía su propio catch→mock, la excepción a la regla.
  return withApiFallback(
    async () => {
      const history = await ApiClient.getMoodHistory(1);
      const hoy = new Date().toISOString().split('T')[0];
      return history.find(e => e.fecha === hoy) ?? null;
    },
    () => MockDataService.getEmocionHoy(),
  );
}

async function getActividadHoyWithApi(): Promise<Awaited<ReturnType<MockService['getActividadHoy']>>> {
  return withApiFallback(() => ApiClient.getActivityToday(), () => MockDataService.getActividadHoy());
}

async function getSemanaActividadWithApi(): Promise<
  Awaited<ReturnType<MockService['getSemanaActividad']>>
> {
  return withApiFallback(
    () => ApiClient.getActivityWeek(),
    () => MockDataService.getSemanaActividad(),
  );
}

async function getHidratacionHoyWithApi(): Promise<
  Awaited<ReturnType<MockService['getHidratacionHoy']>>
> {
  return withApiFallback(
    () => ApiClient.getHydrationToday(),
    () => MockDataService.getHidratacionHoy(),
  );
}

async function buscarAlimentosWithApi(
  query: string,
): Promise<Awaited<ReturnType<MockService['buscarAlimentos']>>> {
  // En modo mock (tests, demo offline, dev sin backend) no se hace red: solo la
  // BD mock local. searchFoodsExternal() haría fetch en vivo a USDA/FatSecret/
  // OpenFoodFacts, rompiendo el contrato "demoable sin backend".
  if (getDataMode() !== 'api') {
    return MockDataService.buscarAlimentos(query);
  }

  // Modo api: BD local/backend + APIs externas (USDA + Open Food Facts) en paralelo.
  const [local, external] = await Promise.all([
    withApiFallback(
      () => ApiClient.searchFoods(query),
      () => MockDataService.buscarAlimentos(query),
    ),
    searchFoodsExternal(query),
  ]);

  // Fusionar: resultados locales primero (más contextualizados), luego externos
  const seen = new Set(local.map(f => f.id));
  const merged = [...local, ...external.filter(f => !seen.has(f.id))];
  return merged.slice(0, 12);
}

// ── Análisis nutricional por foto ───────────────────────────────────────────

export interface AnalizarComidaOpts {
  mealId?: string;
  /**
   * Foto YA subida. Con esto el análisis NO vuelve a subir la imagen.
   *
   * Es la pieza que cierra el circuito único: antes la pantalla de registro
   * subía la foto por su cuenta y el análisis la subía OTRA VEZ por la URL
   * firmada, dejando dos objetos en el bucket por cada comida fotografiada.
   */
  photoId?: string;
  /** Objetivo del usuario, para el sesgo de seguridad del mock. */
  goal?: UserGoal;
  userNote?: string;
  /** Peso o tamaño del plato en texto libre (p. ej. "200g", "plato grande"). */
  plateSizeHint?: string;
  /** Callback de transición de estado (queued → processing → completed). */
  onStatus?: (status: AnalysisStatus) => void;
  signal?: AbortSignal;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

/** Simulación local del análisis (modo mock / fallback): demoable sin backend. */
async function analizarMock(opts: AnalizarComidaOpts): Promise<NutritionAnalysisResult> {
  opts.onStatus?.('queued');
  await sleep(120, opts.signal);
  opts.onStatus?.('processing');
  await sleep(250, opts.signal);
  opts.onStatus?.('completed');
  return buildMockResult(opts.goal);
}

/**
 * Crea el job de análisis y hace polling hasta que termina.
 *
 * Si `opts.photoId` viene, la foto YA está subida y no se vuelve a subir: es el
 * caso normal desde la pantalla de registro. Sin él, sube primero — sigue siendo
 * un único circuito (el del backend), solo que iniciado aquí.
 *
 * En modo 'api' usa el backend real; si no está configurado o falla en cualquier
 * paso, cae a la simulación local (modo demo) de forma transparente.
 */
export async function analizarComidaFoto(
  photo: string,
  opts: AnalizarComidaOpts = {},
): Promise<NutritionAnalysisResult> {
  if (getDataMode() !== 'api' || !ApiClient.isConfigured()) {
    return analizarMock(opts);
  }
  try {
    opts.onStatus?.('queued');

    // ── Preparación de la entrada del modelo (ADR-016) ───────────────────
    // Portero de calidad + recorte al plato + estimador previo. Va ANTES de
    // crear el trabajo, que es lo que la hace útil: una foto inanalizable deja
    // de costar una llamada al modelo y ~20 s de espera del paciente.
    //
    // `prepararEntradaModelo` no lanza nunca: si el navegador no puede hacer
    // nada devuelve un veredicto neutro y todo sigue como antes de esta capa.
    const { prepararEntradaModelo } = await import('@/lib/entrada-modelo');
    const preparacion = await prepararEntradaModelo(photo);
    if (preparacion.calidad.veredicto === 'rechaza') {
      // No se crea el trabajo. Se le dice al paciente qué pasa y qué hacer.
      throw new Error(mensajeDeRechazo(preparacion.calidad));
    }

    const photoId = opts.photoId ?? (await ApiClient.uploadPhoto(photo, { mealId: opts.mealId })).photoId;
    let job = await ApiClient.createAnalysis({
      photoId,
      mealId: opts.mealId,
      userNote: opts.userNote,
      plateSizeHint: opts.plateSizeHint,
      estimacionPrevia: preparacion.estimacionPrevia
        ? aTransmitida(
            preparacion.estimacionPrevia,
            preparacion.entrada?.recortada ? preparacion.entrada.recorte : undefined,
          )
        : undefined,
      entradaModelo: aEntradaTransmitida(preparacion.entrada),
    });

    // Polling con backoff exponencial + jitter hasta un deadline (~30 s). Cada
    // GET /nutrition-analyses/:id tiene coste, así que en vez de martillear a
    // intervalo fijo (antes: 20 × 1.5 s = 20 peticiones) arrancamos en ~1 s y
    // crecemos hasta un tope de 5 s: mismo presupuesto temporal, ~la mitad de
    // peticiones. El jitter evita sincronía entre clientes (thundering herd).
    const DEADLINE_MS = 30_000;
    const POLL_BASE_MS = 1_000;
    const POLL_MAX_MS = 5_000;
    const startedAt = Date.now();
    let attempt = 0;
    let lastStatus: AnalysisStatus | undefined;
    while (
      (job.status === 'queued' || job.status === 'processing') &&
      Date.now() - startedAt < DEADLINE_MS
    ) {
      if (job.status !== lastStatus) { opts.onStatus?.(job.status); lastStatus = job.status; }
      const backoff = Math.min(POLL_BASE_MS * 2 ** attempt, POLL_MAX_MS);
      const jitter = backoff * 0.25 * Math.random();
      await sleep(backoff + jitter, opts.signal);
      job = await ApiClient.getAnalysis(job.id);
      attempt += 1;
    }

    if (job.status === 'completed' && job.result) {
      opts.onStatus?.('completed');
      // Los avisos del portero van al MISMO sitio que los del modelo: al
      // paciente le da igual quién notó que la foto salió movida; lo que
      // necesita saber es que la cifra se calculó sobre una foto mejorable.
      const avisos = avisosDeCalidad(preparacion.calidad);
      if (avisos.length === 0) return job.result;
      return { ...job.result, warnings: [...(job.result.warnings ?? []), ...avisos] };
    }
    // Traducir el errorCode estructurado (NON_FOOD_IMAGE, ANALYSIS_STALE, …) a un
    // mensaje claro para el usuario en vez de propagar el texto crudo del backend.
    throw new Error(
      analysisErrorMessage(job.errorCode, job.error ?? `Análisis no completado (estado: ${job.status})`),
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // En api mode NO caemos a mock: fabricar macros "reales" ante un fallo del
    // backend sería engañoso en una app de salud (el usuario los tomaría por
    // ciertos). Propagamos el error para que la UI lo muestre y el usuario
    // reintente o rellene a mano. El mock solo aplica en data_mode='mock'.
    console.warn('[DataAdapter] análisis IA falló contra el backend:', err);
    throw err instanceof Error ? err : new Error('El análisis no se pudo completar');
  }
}

// ── Consentimiento RGPD ─────────────────────────────────────────────────────

/** Consentimientos sin los que la app no opera (el backend devuelve 403). */
export const REQUIRED_CONSENTS: ConsentType[] = [
  'terms_of_service',
  'privacy_policy',
  'health_data_processing',
  'image_storage',
];
export const CONSENTS_VERSION = '1.0';

const CONSENT_CACHE_KEY = 'helpydoc_consents_ok';
const CONSENT_CACHE_TTL_MS = 7 * 24 * 3600 * 1000; // 7 días

function _getConsentCache(userId: string): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_CACHE_KEY);
    if (!raw) return false;
    const { uid, ts } = JSON.parse(raw) as { uid: string; ts: number };
    return uid === userId && Date.now() - ts < CONSENT_CACHE_TTL_MS;
  } catch { return false; }
}

function _setConsentCache(userId: string): void {
  try {
    localStorage.setItem(CONSENT_CACHE_KEY, JSON.stringify({ uid: userId, ts: Date.now() }));
  } catch { /* ignore QuotaExceededError */ }
}

/**
 * ¿El usuario concedió TODOS los consentimientos requeridos?
 * Usa caché localStorage (7 días) para evitar una petición al API en cada login,
 * lo que también evita la condición de carrera con la hidratación de Firebase Auth.
 */
export async function tieneConsentimientosRequeridos(): Promise<boolean> {
  if (getDataMode() !== 'api' || !ApiClient.isConfigured()) return true;
  const userId = AuthService.getSession()?.userId;
  // Ruta rápida: consentimiento ya cacheado para este usuario
  if (userId && _getConsentCache(userId)) return true;
  try {
    const consents = await ApiClient.listConsents();
    const granted = new Set(
      consents.filter(c => c.status === 'granted').map(c => c.consentType),
    );
    const ok = REQUIRED_CONSENTS.every(t => granted.has(t));
    if (ok && userId) _setConsentCache(userId);
    return ok;
  } catch (err) {
    console.warn('[DataAdapter] no se pudo comprobar consentimientos:', err);
    // En error de red: si hay caché del usuario (aunque expirada), confiar en ella
    if (userId) {
      try {
        const raw = localStorage.getItem(CONSENT_CACHE_KEY);
        if (raw) {
          const { uid } = JSON.parse(raw) as { uid: string };
          if (uid === userId) return true;
        }
      } catch { /* ignore */ }
    }
    return false;
  }
}

/** Concede todos los consentimientos requeridos. No-op en modo mock. */
export async function concederConsentimientosRequeridos(): Promise<void> {
  if (getDataMode() !== 'api' || !ApiClient.isConfigured()) return;
  for (const consentType of REQUIRED_CONSENTS) {
    await ApiClient.createConsent({ consentType, status: 'granted', version: CONSENTS_VERSION });
  }
  const userId = AuthService.getSession()?.userId;
  if (userId) _setConsentCache(userId);
}

// ── HD-040 · Firma electrónica del consentimiento (Email OTP) ────────────────
// Orquesta mock (dev/demo, localStorage) y api (contrato HD-040 §3). Sin
// fachadas en modo api: en `api` se llama SIEMPRE al backend real.

/** Versión legal vigente del documento de inscripción firmado. */
export const CONSENT_DOCUMENT_VERSION = 'consent.v1';

/** Obligatorios para firmar (sin ellos la app no opera). */
export const SIGN_REQUIRED_CONSENTS: ConsentType[] = [
  'terms_of_service',
  'privacy_policy',
  'health_data_processing',
  'image_storage',
];
/** Opcionales del hito de inscripción (premium `medical_consultation` va aparte). */
export const SIGN_OPTIONAL_CONSENTS: ConsentType[] = [
  'image_calibration',
  'research_participation',
  'marketing',
];

const MOCK_SIG_KEY = 'helpydoc_mock_consent_sig';
const MOCK_OTP_KEY = 'helpydoc_mock_consent_otp';

interface MockOtpRecord {
  requestId: string;
  code: string;
  expiresAt: number;
  documentVersion: string;
}

/**
 * ¿El flujo de FIRMA (HD-040) está activo?
 * - mock: siempre (dev/demo ejercitan la firma completa).
 * - api: solo si `VITE_CONSENT_SIGNATURE === 'on'`. Se activa cuando el backend
 *   HD-040 (`/consents/sign/*`) esté desplegado; hasta entonces el gate legacy
 *   (`tieneConsentimientosRequeridos`) sigue vigente en producción para no
 *   romper el login. Coordinación HD-040: «arranca en mock, conmuta a api».
 */
export function consentSignatureEnabled(): boolean {
  if (getDataMode() !== 'api') return true;
  return import.meta.env.VITE_CONSENT_SIGNATURE === 'on';
}

function _isMockConsent(): boolean {
  return getDataMode() !== 'api' || !ApiClient.isConfigured();
}

export interface ConsentOtpRequest {
  requestId: string;
  expiresInSec: number;
  /** Solo en modo mock: el código real, para mostrarlo como pista de desarrollo. */
  devCode?: string;
}

/** Solicita un código OTP al email del usuario autenticado. */
export async function requestConsentOtp(documentVersion: string): Promise<ConsentOtpRequest> {
  if (_isMockConsent()) {
    const code = String(Math.floor(100000 + Math.random() * 900000));
    const rec: MockOtpRecord = {
      requestId: `mock-${Date.now()}`,
      code,
      expiresAt: Date.now() + 10 * 60 * 1000,
      documentVersion,
    };
    try { localStorage.setItem(MOCK_OTP_KEY, JSON.stringify(rec)); } catch { /* ignore */ }
    return { requestId: rec.requestId, expiresInSec: 600, devCode: code };
  }
  return ApiClient.requestConsentOtp(documentVersion);
}

/** Verifica el OTP y crea el registro de firma + consents individuales. */
export async function verifyConsentSignature(input: {
  requestId: string;
  code: string;
  consents: ConsentDecisionMap;
  documentVersion: string;
}): Promise<ConsentSignature> {
  if (_isMockConsent()) {
    let rec: MockOtpRecord | null;
    try {
      const raw = localStorage.getItem(MOCK_OTP_KEY);
      rec = raw ? (JSON.parse(raw) as MockOtpRecord) : null;
    } catch { rec = null; }
    if (!rec || rec.requestId !== input.requestId) {
      throw new Error('No encontramos tu solicitud de firma. Reenvía el código.');
    }
    if (Date.now() > rec.expiresAt) {
      throw new Error('El código ha caducado. Reenvía uno nuevo.');
    }
    if (rec.code !== input.code) {
      throw new Error('Código incorrecto. Revisa el email o reenvía.');
    }
    const sig: ConsentSignature = {
      signatureId: `mocksig-${Date.now()}`,
      signedAt: new Date().toISOString(),
      documentVersion: input.documentVersion,
      method: 'email_otp',
      consents: input.consents,
      documentHash: `sha256:${rec.requestId.slice(-8)}${input.code}`,
      emailChannel: AuthService.getSession()?.email,
    };
    try {
      localStorage.setItem(MOCK_SIG_KEY, JSON.stringify(sig));
      localStorage.removeItem(MOCK_OTP_KEY);
    } catch { /* ignore */ }
    return sig;
  }
  const res = await ApiClient.verifyConsentSignature(input);
  // Tras firmar, refrescar la firma vigente desde el backend (fuente de verdad).
  const current = await ApiClient.getConsentSignatureCurrent();
  return current ?? {
    signatureId: res.signatureId,
    signedAt: res.signedAt,
    documentVersion: input.documentVersion,
    method: 'email_otp',
    consents: input.consents,
    signedDocumentUrl: res.signedDocumentUrl,
  };
}

/** Devuelve la firma vigente del usuario, o null si no ha firmado. */
export async function getConsentSignature(): Promise<ConsentSignature | null> {
  if (_isMockConsent()) {
    try {
      const raw = localStorage.getItem(MOCK_SIG_KEY);
      return raw ? (JSON.parse(raw) as ConsentSignature) : null;
    } catch { return null; }
  }
  return ApiClient.getConsentSignatureCurrent();
}

/**
 * Gate HD-040: ¿el usuario tiene una firma válida de la versión legal vigente?
 * true → entra directo (no se re-pide). false → falta firma o cambió la versión.
 * Ante error de red devuelve false (fail-safe hacia la pantalla de firma).
 */
export async function hasValidConsentSignature(): Promise<boolean> {
  try {
    const sig = await getConsentSignature();
    return !!sig && sig.documentVersion === CONSENT_DOCUMENT_VERSION;
  } catch (err) {
    console.warn('[DataAdapter] no se pudo comprobar la firma de consentimiento:', err);
    return false;
  }
}

/** Reenvía por email la copia PDF firmada. No-op en mock. */
export async function resendConsentCopy(): Promise<void> {
  if (_isMockConsent()) return;
  await ApiClient.resendConsentCopy();
}

/**
 * Actualiza un consentimiento OPCIONAL sin re-firmar (Ajustes › Privacidad).
 * Solo válido para los opcionales; los obligatorios forman parte de la firma.
 */
export async function setOptionalConsent(consentType: ConsentType, granted: boolean): Promise<void> {
  if (!SIGN_OPTIONAL_CONSENTS.includes(consentType)) {
    throw new Error(`El consentimiento ${consentType} no es gestionable sin re-firmar.`);
  }
  if (_isMockConsent()) {
    try {
      const raw = localStorage.getItem(MOCK_SIG_KEY);
      if (raw) {
        const sig = JSON.parse(raw) as ConsentSignature;
        sig.consents = { ...sig.consents, [consentType]: granted ? 'granted' : 'denied' };
        localStorage.setItem(MOCK_SIG_KEY, JSON.stringify(sig));
      }
    } catch { /* ignore */ }
    return;
  }
  await ApiClient.createConsent({
    consentType,
    status: granted ? 'granted' : 'revoked',
    version: CONSENT_DOCUMENT_VERSION,
  });
}

export const DataService: MockService = {
  ...MockDataService,
  getUsuario: getUsuarioWithApi,
  getPesoHistorico: () =>
    withApiFallback(() => ApiClient.getPesoHistorico(), () => MockDataService.getPesoHistorico()),
  // En modo api computa el progreso desde el perfil REAL (antes heredaba el del
  // mock, que usaba getUsuario del mock → % de peso falso en Perfil). Guarda
  // contra datos ausentes/rango 0 para no renderizar NaN.
  getPesoProgreso: async () => {
    const u = await withApiFallback(() => ApiClient.getMe(), () => MockDataService.getUsuario());
    const ini = Number(u.pesoInicial);
    const act = Number(u.pesoActual);
    const obj = Number(u.pesoObjetivo);
    if (![ini, act, obj].every(Number.isFinite)) {
      return { pesoActual: act || 0, pesoObjetivo: obj || 0, perdido: 0, restante: 0, pct: 0 };
    }
    const perdido = +(ini - act).toFixed(1);
    const restante = +(act - obj).toFixed(1);
    const rango = ini - obj;
    const pct = rango === 0 ? 100 : Math.max(0, Math.min(100, Math.round((perdido / rango) * 100)));
    return { pesoActual: act, pesoObjetivo: obj, perdido, restante, pct };
  },
  // Catálogo de platos desde el backend (antes siempre mock, incluso en api).
  getPlatosSaludables: (filtros = {}) =>
    withApiFallback(() => ApiClient.getPlatos(filtros), () => MockDataService.getPlatosSaludables(filtros)),
  getResumenNutricionHoy: getResumenNutricionHoyWithApi,
  getDashboardResumen: getDashboardResumenWithApi,
  getComidasHoy: getComidasHoyWithApi,
  getHistorialEmocional: getHistorialEmocionalWithApi,
  getEmocionHoy: getEmocionHoyWithApi,
  getActividadHoy: getActividadHoyWithApi,
  getSemanaActividad: getSemanaActividadWithApi,
  getHidratacionHoy: getHidratacionHoyWithApi,
  getComidasRecientes: (n = 5) =>
    withApiFallback(
      async () => {
        const [apiMeals, lsMeals] = await Promise.all([
          ApiClient.getMeals({ pageSize: n }),
          MockDataService.getComidasRecientes(n),
        ]);
        if (apiMeals.length === 0) return apiMeals;
        const fotosMap = new Map(lsMeals.map(m => [m.id, m.foto]));
        return apiMeals.map(m => ({ ...m, foto: m.foto ?? fotosMap.get(m.id) }));
      },
      () => MockDataService.getComidasRecientes(n),
    ),
  getAllComidas: () =>
    withApiFallback(
      async () => {
        const [apiMeals, lsMeals] = await Promise.all([
          ApiClient.getMeals({ pageSize: 100 }), // backend max = 100
          MockDataService.getAllComidas(), // ya incluye fotos de _loadPhotoLS
        ]);
        // Array vacío es una respuesta válida (sin historial en el backend).
        // Solo caemos a localStorage cuando la API lanza (lo gestiona withApiFallback).
        if (apiMeals.length === 0) return apiMeals;
        const fotosMap = new Map(lsMeals.map(m => [m.id, m.foto]));
        const merged = apiMeals.map(m => ({ ...m, foto: m.foto ?? fotosMap.get(m.id) }));
        // Persistir URLs de foto obtenidas de la API en localStorage para acceso offline.
        for (const m of merged) {
          if (m.foto && !fotosMap.get(m.id)) {
            void MockDataService.actualizarFotoComida(m.id, m.foto);
          }
        }
        return merged;
      },
      () => MockDataService.getAllComidas(),
    ),
  guardarComida: (meal) =>
    withApiFallback(
      async () => {
        const saved = await ApiClient.saveMeal(meal);
        // Guardar en localStorage con el mismo id/fecha/hora que devolvió la API,
        // para que getAllComidas pueda encontrar la foto por ID cuando cargue desde API.
        return MockDataService.guardarComida({ ...meal, id: saved.id, fecha: saved.fecha, hora: saved.hora });
      },
      () => MockDataService.guardarComida(meal),
    ),
  // Edición de una comida guardada (nombre/macros). Persiste en el backend con
  // PATCH /meals/:id y refleja en local. El `analisis` (desglose) no lo guarda el
  // backend; se conserva en local con el mismo factor de escala.
  actualizarComida: (
    id: string,
    patch: Parameters<typeof MockDataService.actualizarComida>[1],
  ) =>
    withApiFallback(
      async () => {
        await ApiClient.updateMeal(id, patch);
        return MockDataService.actualizarComida(id, patch);
      },
      () => MockDataService.actualizarComida(id, patch),
    ),
  guardarEmocion: (mood) =>
    withApiFallback(
      async () => { await ApiClient.saveMood(mood); return MockDataService.guardarEmocion(mood); },
      () => MockDataService.guardarEmocion(mood),
    ),
  guardarEjercicio: (ej) =>
    withApiFallback(
      async () => { await ApiClient.saveExercise(ej); return MockDataService.guardarEjercicio(ej); },
      () => MockDataService.guardarEjercicio(ej),
    ),
  actualizarHidratacion: (ml) =>
    withApiFallback(
      () => ApiClient.updateHydration(ml),
      () => MockDataService.actualizarHidratacion(ml),
    ),
  actualizarUsuario: (changes) =>
    withApiFallback(
      () => ApiClient.updateMe(changes),
      () => MockDataService.actualizarUsuario(changes),
    ),
  actualizarPeso: (kg) =>
    withApiFallback(
      async () => {
        // Persistir en el backend: (1) entrada en la serie temporal de peso
        // (POST /weight) y (2) el peso actual del perfil (PATCH /users/me),
        // para que getPesoHistorico y getMe reflejen el cambio tras recargar.
        const fecha = new Date().toISOString().slice(0, 10);
        await ApiClient.saveWeight(fecha, kg);
        const usuario = await ApiClient.updateMe({ pesoActual: kg });
        // Mantener localStorage en sync para acceso offline / fallback.
        void MockDataService.actualizarPeso(kg);
        return usuario;
      },
      () => MockDataService.actualizarPeso(kg),
    ),
  buscarAlimentos: buscarAlimentosWithApi,
  contribuirAlimento: (food) =>
    withApiFallback(
      async () => { await ApiClient.contribuirAlimento(food); return MockDataService.contribuirAlimento(food); },
      () => MockDataService.contribuirAlimento(food),
    ),
  actualizarFotoComida: (mealId, photoUrl) => {
    // Persistir foto URL en el backend (best-effort) y en localStorage
    void ApiClient.updateMealFoto(mealId, photoUrl).catch(() => {});
    return MockDataService.actualizarFotoComida(mealId, photoUrl);
  },
  borrarFotoComida: async (mealId) => {
    // Limpiar foto en backend (best-effort) y en localStorage
    void ApiClient.updateMealFoto(mealId, '').catch(() => {});
    const fotoAnterior = await MockDataService.borrarFotoComida(mealId);
    // Y el objeto de Storage al que apuntaba: quitar solo la referencia deja una
    // foto de comida en el bucket que ya nadie puede alcanzar — ni para verla ni
    // para borrarla.
    const session = AuthService.getSession();
    if (session?.userId) {
      void deleteMealPhoto(session.userId, mealId, fotoAnterior).catch(() => {});
    }
    return fotoAnterior;
  },
  borrarComida: (id) =>
    withApiFallback(
      async () => {
        try {
          await ApiClient.deleteMeal(id);
        } catch (err) {
          // Un 404 al borrar NO es un fallo: lo que se pide —que esa comida deje
          // de existir— ya se cumple. Y pasa de verdad: una comida guardada solo
          // en local (sin red, o antes de que la cuenta sincronizara) tiene un id
          // que el servidor no conoce, el DELETE respondía 404, se trataba como
          // error y la copia local NUNCA se borraba. Resultado: una comida
          // ATRAPADA, imposible de quitar desde la app por mucho que se insista.
          // Borrar es idempotente; solo los demás errores son errores.
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
        return borrarComidaYSuFoto(id);
      },
      () => borrarComidaYSuFoto(id),
    ),
};

/**
 * Borra la comida en local y, con la URL que devuelve, el objeto de Storage.
 *
 * ORDEN DELIBERADO: primero el borrado local, porque es el que DEVUELVE la URL
 * de la foto. Antes se borraba la foto primero, con `uid + mealId` — y esa ruta
 * no existía nunca (el objeto se llama por marca de tiempo y termina en `.webp`,
 * no en `.jpg`), así que el borrado fallaba en silencio y la foto sobrevivía a
 * la comida. Está en las dos ramas —con API y sin ella— porque el objeto está en
 * Storage en ambos casos: el fallback solo evita el backend, no Firebase.
 */
async function borrarComidaYSuFoto(id: string): Promise<string | undefined> {
  const fotoAnterior = await MockDataService.borrarComida(id);
  const session = AuthService.getSession();
  if (session?.userId) {
    void deleteMealPhoto(session.userId, id, fotoAnterior).catch(() => {});
  }
  return fotoAnterior;
}

// ── HD-045 · Asistente del programa (chatbot RAG, B4) ───────────────────────
/**
 * Pregunta al asistente del programa.
 *
 * Modo mock (`VITE_DATA_MODE=mock`, o `api` sin `VITE_API_BASE_URL`):
 * responde con una heurística de DEMO local, SIN red y sin llamar a ningún
 * proveedor real (regla dura #8 del encargo HD-045-B4) — ver
 * `respuestaAsistenteMock` en `@/lib/asistente` para lo que decide y, sobre
 * todo, para lo que NO es: no reimplementa ni sustituye la puerta clínica
 * real (`packages/contracts/src/asistente.ts`, B1), que solo vive en el
 * backend.
 *
 * Modo api: llama al backend real (`POST /asistente/preguntas`). Un
 * `ApiError` (404 flag de servidor apagado, 429 límite, 401 sin sesión…) se
 * propaga TAL CUAL para que la UI distinga cada caso — a diferencia de
 * `withApiFallback`, aquí no hay un mock de emergencia razonable que enseñar
 * ante un fallo real de conversación (mostrar una respuesta de mentira como
 * si fuera del asistente sería peor que fallar visiblemente).
 */
export async function preguntarAsistente(input: PreguntaAsistenteInput): Promise<RespuestaAsistente> {
  if (getDataMode() !== 'api' || !ApiClient.isConfigured()) {
    return respuestaAsistenteMock(input);
  }
  return ApiClient.preguntarAsistente(input);
}

export { getDataMode, setDataMode } from '@/config/data-mode';
export type { DataMode } from '@/config/data-mode';
